import {
  DRONE_SITES_ENTITLEMENT_KEYS,
  isEntitlementAllowed,
  isTreeTabInferenceToolAllowed,
  type LayerTogglePanelTabId,
} from '../../../config/analysisLayerEntitlements';
import { getDroneProvinceNavEntries } from '../../shared/constants/droneSites';

/** One section button shown in the header while a large-scale tool is open. */
export interface LargeScaleNavSection {
  id: string;
  label: string;
  /** Map visibility keys cleared on close, and turned on when `autoEnable` is set. */
  layerKeys: string[];
  /** Extra catalog keys that unlock the button. Defaults to `layerKeys`. */
  entitlementKeys?: string[];
  /** Shown whenever the parent tool is open (for example a coming-soon card). */
  always?: boolean;
  /** Turn the map layer on as soon as this section is opened. */
  autoEnable: boolean;
  /** Planting-method years: enable only the first entitled season. */
  enableFirstOnly?: boolean;
}

const DRONE_SAMPLE_NAV_SECTIONS: LargeScaleNavSection[] = [
  {
    id: 'isabela',
    label: 'Isabela PhilRice',
    layerKeys: ['isabelaPhilriceNDVI', 'isabelaPhilriceOrtho'],
    autoEnable: false,
  },
  {
    id: 'cavite',
    label: 'Cavite Silang',
    layerKeys: ['caviteSilangNDVI', 'caviteSilangOrtho'],
    autoEnable: false,
  },
  ...getDroneProvinceNavEntries().map(
    ({ id, label }): LargeScaleNavSection => ({
      id,
      label,
      layerKeys: [],
      entitlementKeys: [...DRONE_SITES_ENTITLEMENT_KEYS],
      autoEnable: false,
    }),
  ),
];

const LARGE_SCALE_NAV_SECTIONS: Partial<Record<LayerTogglePanelTabId, LargeScaleNavSection[]>> = {
  multiTemporal: [
    { id: 'cadi', label: 'El Niño CADI Drought Forecast', layerKeys: ['cadiDrought'], autoEnable: true },
    { id: 'pest', label: 'Pest Occurrence Probability Index', layerKeys: ['pestRisk'], autoEnable: true },
    {
      id: 'planting',
      label: 'Rice Planting Method',
      layerKeys: ['plantingMethodDry2025', 'plantingMethodDry2024'],
      autoEnable: true,
      enableFirstOnly: true,
    },
    {
      id: 'growthStage',
      label: 'Rice Crop Growth Stage',
      layerKeys: ['riceGrowthStage'],
      autoEnable: true,
    },
    {
      id: 'irrigation',
      label: 'Irrigation Type',
      layerKeys: ['irrigationType'],
      autoEnable: true,
    },
    { id: 'lst', label: 'Land Surface Temperature', layerKeys: ['lst'], autoEnable: true },
    { id: 'vhi', label: 'Agricultural Drought (VHI)', layerKeys: ['vhi'], autoEnable: true },
    { id: 'bsri', label: 'Black Sigatoka Risk', layerKeys: ['bsri'], autoEnable: true },
  ],
  forecast: [
    {
      id: 'riceForecast',
      label: 'Rice Yield Forecast',
      layerKeys: ['riceForecast'],
      entitlementKeys: ['riceForecast', 'rice_yield_forecast'],
      autoEnable: false,
    },
  ],
  droneData: DRONE_SAMPLE_NAV_SECTIONS,
  cropArea: [
    { id: 'maize', label: 'Maize Area Map', layerKeys: ['maize'], autoEnable: true },
    { id: 'sugarCane', label: 'Sugarcane Area Map', layerKeys: ['sugarCane'], autoEnable: true },
    { id: 'rice', label: 'Rice Area Map', layerKeys: ['rice'], autoEnable: true },
    { id: 'coconut', label: 'Coconut Area Map', layerKeys: ['coconut'], autoEnable: true },
  ],
  disasterLossDamage: [
    {
      id: 'flood',
      label: 'Typhoon and Drought Loss & Damage Estimates',
      layerKeys: [],
      entitlementKeys: ['floodDamage', 'disasterLossDamage', 'flood'],
      autoEnable: false,
    },
  ],
  lowCarbon: [
    { id: 'awd', label: 'AWD maps', layerKeys: [], always: true, autoEnable: false },
    { id: 'straw', label: 'Rice Straw Burning', layerKeys: ['strawBurn'], autoEnable: false },
  ],
  fieldServices: [
    ...DRONE_SAMPLE_NAV_SECTIONS,
    {
      id: 'fieldSensorCh4',
      label: 'CH4 & AWD sensors',
      layerKeys: [],
      always: true,
      autoEnable: false,
    },
    {
      id: 'fieldSensorSoil',
      label: 'Soil & nutrient sensors',
      layerKeys: [],
      always: true,
      autoEnable: false,
    },
  ],
  treeDetection: [
    { id: 'trees', label: 'Tree Detection', layerKeys: ['treeDetection'], autoEnable: false },
    { id: 'crowns', label: 'Crown Segmentation', layerKeys: ['crownSegmentation'], autoEnable: false },
    { id: 'fields', label: 'Field boundary delineation', layerKeys: ['cropfieldDelineation'], autoEnable: false },
  ],
};

function sectionUnlocked(
  tab: LayerTogglePanelTabId,
  section: LargeScaleNavSection,
  set: Set<string> | null,
): boolean {
  if (section.always) return true;
  if (tab === 'treeDetection') {
    if (section.id === 'trees') return isTreeTabInferenceToolAllowed(set, 'tree');
    if (section.id === 'crowns') return isTreeTabInferenceToolAllowed(set, 'crown');
    if (section.id === 'fields') return isTreeTabInferenceToolAllowed(set, 'field');
  }
  if (set == null) return true;
  const keys = section.entitlementKeys ?? section.layerKeys;
  if (keys.length === 0) return true;
  return keys.some((key) => isEntitlementAllowed(set, key));
}

export function visibleLargeScaleNavSections(
  tab: string | null | undefined,
  set: Set<string> | null,
): LargeScaleNavSection[] {
  if (!tab || tab === 'farm') return [];
  const list = LARGE_SCALE_NAV_SECTIONS[tab as LayerTogglePanelTabId];
  if (!list) return [];
  return list.filter((section) => sectionUnlocked(tab as LayerTogglePanelTabId, section, set));
}

/** Keys to turn on when a section is opened. Other keys in the tool stay off. */
export function layerKeysToEnable(
  section: LargeScaleNavSection,
  set: Set<string> | null,
): string[] {
  if (!section.autoEnable || section.layerKeys.length === 0) return [];
  const entitled = section.layerKeys.filter((key) => isEntitlementAllowed(set, key));
  if (entitled.length === 0) return [];
  if (section.enableFirstOnly) return [entitled[0]];
  return entitled;
}
